/**
 * B52 — tenant theming as channel configuration (spec §9.2 posture: theming
 * is configuration, not new commerce machinery).
 *
 * A ChannelTheme carries a brand logo (https only) and color overrides for
 * a FIXED vocabulary of roles — exactly the --transact-* custom properties
 * the surfaces already consume (@transact/ui tokens.css; the embed bridge
 * has mapped host themes onto the same vars since B31a). One mapping,
 * themeCssVars, feeds every surface, so a theme cannot invent tokens.
 *
 * Contrast walls (WCAG 2.x): a brand must never make text unreadable. The
 * walls check EFFECTIVE colors — the package defaults overlaid by the
 * theme — but only pairs the theme TOUCHES: the stock design language is
 * B60's call, not the operator's, so an untouched pair is never the
 * operator's violation. Text pairs hold 4.5:1 (AA); accent-label and
 * muted pairs hold 3:1 (AA large-text/UI-component floor — the stock
 * accent pair sits just under 3, which is precisely why untouched pairs
 * are out of scope).
 *
 * THEME_DEFAULTS duplicates tokens.css values by necessity (domain stays
 * pure — no I/O); the theming test parses tokens.css and refuses drift.
 */

/* ----------------------------------------------------------------- shape */

export const CHANNEL_THEME_ROLES = [
  "accent",
  "accentStrong",
  "accentContrast",
  "accentSoft",
  "accentText",
  "ink",
  "onInk",
  "bg",
  "surface",
  "text",
  "muted",
  "border",
] as const;

export type ChannelThemeRole = (typeof CHANNEL_THEME_ROLES)[number];

export interface ChannelTheme {
  /** Brand logo, https only — surfaces render it, so no data:/http: smuggling. */
  readonly logoUrl?: string;
  readonly colors?: Partial<Record<ChannelThemeRole, string>>;
}

/** tokens.css values (drift-walled by test — see module doc). */
export const THEME_DEFAULTS: Record<ChannelThemeRole, string> = Object.freeze({
  accent: "#3d7fe8",
  accentStrong: "#2f6fd4",
  accentContrast: "#ffffff",
  accentSoft: "#e6effd",
  accentText: "#245aa8",
  ink: "#1c2a4a",
  onInk: "#ffffff",
  bg: "#f2f4f8",
  surface: "#ffffff",
  text: "#1c2a4a",
  muted: "#5c6880",
  border: "#e6e9f0",
});

/**
 * B79 — the dark set (tokens.css [data-transact-theme="dark"], drift-walled
 * by test). Brand hints are single-valued and land in BOTH themes, so a
 * hint must survive both: each wall below runs against each default set.
 */
export const THEME_DEFAULTS_DARK: Record<ChannelThemeRole, string> = Object.freeze({
  accent: "#568de9",
  accentStrong: "#4a7fd6",
  accentContrast: "#ffffff",
  accentSoft: "#1c2f4f",
  accentText: "#a9c8ff",
  ink: "#e8ebf4",
  onInk: "#10141f",
  bg: "#101420",
  surface: "#222630",
  text: "#ffffff",
  muted: "#a89fb8",
  border: "#3d4557",
});

/* -------------------------------------------------------------- contrast */

const HEX = /^#[0-9a-fA-F]{6}$/;

function linear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG 2.x contrast ratio, 1..21, order-independent. Requires 6-digit hex. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/* ------------------------------------------------------------ validation */

/** [foreground role, background role, minimum ratio] — see module doc. */
const CONTRAST_WALLS: readonly (readonly [ChannelThemeRole, ChannelThemeRole, number])[] = [
  ["text", "surface", 4.5],
  ["text", "bg", 4.5],
  ["ink", "surface", 4.5],
  ["muted", "surface", 3],
  ["accentContrast", "accent", 3],
  ["accentContrast", "accentStrong", 3],
  // B83: surface ⇄ paired foreground — a hint repainting one side of a
  // pair must keep the other side readable, per theme (a light accentSoft
  // is exactly Pathik's unreadable selected radio row in dark mode).
  ["accentText", "accentSoft", 4.5],
  ["onInk", "ink", 4.5],
];

const ROLE_SET: ReadonlySet<string> = new Set(CHANNEL_THEME_ROLES);

/** Repo style: every violation reported in one pass; [] means valid. */
export function validateChannelTheme(theme: ChannelTheme | undefined): readonly string[] {
  if (theme === undefined) return [];
  const violations: string[] = [];

  if (theme.logoUrl !== undefined) {
    let ok = false;
    try {
      ok = new URL(theme.logoUrl).protocol === "https:";
    } catch {
      ok = false;
    }
    if (ok === false) violations.push(`logoUrl must be a valid https URL: ${theme.logoUrl}`);
  }

  const colors = theme.colors ?? {};
  const touched = new Set<string>();
  for (const [role, value] of Object.entries(colors)) {
    if (ROLE_SET.has(role) === false) {
      violations.push(`unknown color role: ${role} (roles: ${CHANNEL_THEME_ROLES.join(", ")})`);
      continue;
    }
    if (typeof value !== "string" || HEX.test(value) === false) {
      violations.push(`color ${role} must be 6-digit hex (#rrggbb), got: ${String(value)}`);
      continue;
    }
    touched.add(role);
  }

  // B79: a hint is one value serving BOTH themes — each wall runs against
  // each default set. When the effective pair is identical in both themes
  // (neither default differs or both roles are touched), the dark pass
  // would re-report the same failure — it is skipped as a duplicate.
  const effectiveIn = (defaults: Record<ChannelThemeRole, string>, role: ChannelThemeRole): string =>
    touched.has(role) ? (colors[role] as string) : defaults[role];
  for (const [fg, bg, floor] of CONTRAST_WALLS) {
    if (touched.has(fg) === false && touched.has(bg) === false) continue; // untouched = B60's call
    const lightFg = effectiveIn(THEME_DEFAULTS, fg);
    const lightBg = effectiveIn(THEME_DEFAULTS, bg);
    const lightRatio = contrastRatio(lightFg, lightBg);
    if (lightRatio < floor) {
      violations.push(
        `contrast ${fg} on ${bg} is ${lightRatio.toFixed(2)}:1 — the floor is ${floor}:1 (WCAG 2.x)`,
      );
    }
    const darkFg = effectiveIn(THEME_DEFAULTS_DARK, fg);
    const darkBg = effectiveIn(THEME_DEFAULTS_DARK, bg);
    if (darkFg === lightFg && darkBg === lightBg) continue; // identical pair — already judged
    const darkRatio = contrastRatio(darkFg, darkBg);
    if (darkRatio < floor) {
      violations.push(
        `contrast ${fg} on ${bg} is ${darkRatio.toFixed(2)}:1 in the dark theme — the floor is ${floor}:1 (WCAG 2.x)`,
      );
    }
  }

  return violations;
}

/* --------------------------------------------------------------- mapping */

function kebab(role: string): string {
  return role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/**
 * Role overrides → --transact-* custom-property pairs, in vocabulary order.
 * Colors only: the logo is content (an <img>), not a token.
 */
export function themeCssVars(theme: ChannelTheme | undefined): readonly (readonly [string, string])[] {
  const colors = theme?.colors;
  if (colors === undefined) return [];
  return CHANNEL_THEME_ROLES.filter((role) => colors[role] !== undefined).map(
    (role) => [`--transact-${kebab(role)}`, colors[role] as string] as const,
  );
}
